import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateDonationMutation, type BillingOverview } from '@entities/Plan';
import { cn } from '@shared/lib';
import { PlainField, toast } from '@shared/ui';
import { DAY_MS, PLAN_LABEL, useBillingFormat } from './usePlansView';

const DONATE_MIN_FALLBACK = 1;
const DONATE_PRESETS_FALLBACK = [100, 300, 500];
const FAQ_IDS = ['autoRenew', 'start', 'spend', 'reset', 'upgrade', 'early', 'afterDemo', 'payment'] as const;

type Section = 'faq' | 'history';

interface FaqItem {
  id: (typeof FAQ_IDS)[number];
  q: string;
  a: string;
}

interface HistoryItem {
  /** Момент оплаты для сортировки ленты, мс. */
  at: number;
  key: string;
  date: string;
  /** Время оплаты — в поясе пользователя. */
  time: string;
  what: string;
  note: string;
  sum: string;
  status: string;
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <path d="M6 1.5v9M1.5 6h9" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 3.5 4.5 4.5L6 12.5" />
    </svg>
  );
}

function useFaq(overview: BillingOverview): FaqItem[] {
  const { t } = useTranslation();
  const f = useBillingFormat();
  const now = Date.now();
  const params: Partial<Record<FaqItem['id'], Record<string, unknown>>> = {
    autoRenew: { count: overview.periodDays },
    start: { start: f.longDate(now), end: f.longDate(now + overview.periodDays * DAY_MS) },
    reset: { time: f.mskTime(overview.quota.resetAt) },
  };
  return FAQ_IDS.map((id) => ({
    id,
    q: t(`billing.shop.faq.${id}.q`),
    a: t(`billing.shop.faq.${id}.a`, params[id]),
  }));
}

/**
 * Оплаты тарифа и донаты одной лентой, новые сверху. Строка — один платёж:
 * доплата Pro → Max стоит отдельно от покупки Pro, а не сливается с ней.
 */
function useHistory(overview: BillingOverview): HistoryItem[] {
  const { t } = useTranslation();
  const f = useBillingFormat();
  const plans = overview.planPayments.map((row) => {
    const paid = row.amountRub > 0;
    const what = row.kind === 'upgrade' && row.fromPlan
      ? t('billing.shop.history.upgrade', { from: PLAN_LABEL[row.fromPlan], plan: PLAN_LABEL[row.plan] })
      : row.startsAt && row.endsAt
        ? t('billing.shop.history.item', {
          plan: PLAN_LABEL[row.plan],
          count: Math.round((Date.parse(row.endsAt) - Date.parse(row.startsAt)) / DAY_MS),
        })
        : PLAN_LABEL[row.plan];
    return {
      at: Date.parse(row.createdAt),
      key: `plan-${row.createdAt}-${row.kind}-${row.plan}`,
      date: f.date(row.createdAt),
      time: f.time(row.createdAt),
      what,
      note: row.endsAt ? t('billing.shop.history.until', { date: f.date(row.endsAt) }) : '',
      sum: paid ? f.rub(row.amountRub) : '—',
      status: t(paid ? 'billing.shop.history.paid' : 'billing.shop.history.free'),
    };
  });
  const donations = overview.donations.map((row) => ({
    at: Date.parse(row.createdAt),
    key: `donation-${row.createdAt}`,
    date: f.date(row.createdAt),
    time: f.time(row.createdAt),
    what: t('billing.shop.history.donation'),
    note: t('billing.shop.history.donationNote'),
    sum: f.rub(row.amountRub),
    status: t('billing.shop.history.paid'),
  }));
  return [...plans, ...donations].sort((a, b) => b.at - a.at);
}

interface FaqListProps {
  items: FaqItem[];
  open: number;
  onToggle: (index: number) => void;
}

function FaqList({ items, open, onToggle }: FaqListProps) {
  const base = useId();
  return (
    <div className="pl-faq">
      {items.map((item, index) => {
        const expanded = open === index;
        return (
          <div key={item.id} className="pl-faq-item">
            <h3 className="pl-faq-h">
              <button
                type="button"
                className="pl-faq-q"
                aria-expanded={expanded}
                aria-controls={`${base}-${item.id}`}
                onClick={() => onToggle(index)}
              >
                <span>{item.q}</span>
                <span className="pl-faq-icon"><PlusIcon /></span>
              </button>
            </h3>
            <div id={`${base}-${item.id}`} className="pl-faq-a" hidden={!expanded}>{item.a}</div>
          </div>
        );
      })}
    </div>
  );
}

function HistoryTable({ rows }: { rows: HistoryItem[] }) {
  const { t } = useTranslation();
  if (!rows.length) {
    return (
      <div className="pl-history-empty">
        <p className="pl-history-empty-title">{t('billing.shop.history.empty')}</p>
        <p>{t('billing.shop.history.emptyText')}</p>
      </div>
    );
  }
  return (
    <div className="pl-history">
      <table className="pl-history-table">
        <thead>
          <tr>
            <th scope="col">{t('billing.shop.history.date')}</th>
            <th scope="col">{t('billing.shop.history.what')}</th>
            <th scope="col" className="is-num">{t('billing.shop.history.sum')}</th>
            <th scope="col" className="is-num">{t('billing.shop.history.status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="pl-history-date">{row.date}<span className="pl-history-note">{row.time}</span></td>
              <td>{row.what}<span className="pl-history-note">{row.note}</span></td>
              <td className="is-num pl-history-sum">{row.sum}</td>
              <td className="is-num pl-history-status">{row.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="pl-history-foot">{t('billing.shop.history.receipts')}</p>
    </div>
  );
}

function HistoryList({ rows }: { rows: HistoryItem[] }) {
  const { t } = useTranslation();
  if (!rows.length) return <p className="pl-hlist-empty">{t('billing.shop.history.empty')}</p>;
  return (
    <ul className="pl-hlist">
      {rows.map((row) => (
        <li key={row.key}>
          <span className="pl-hlist-main">
            <span>{row.what}</span>
            <span className="pl-hlist-sub">{[`${row.date}, ${row.time}`, row.note].filter(Boolean).join(' · ')}</span>
          </span>
          <span className="pl-hlist-side">
            <span className="pl-hlist-sum">{row.sum}</span>
            <span className="pl-hlist-status">{row.status}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Desktop: вкладки, по умолчанию открыт первый вопрос. */
function DetailsTabs({ faq, history }: { faq: FaqItem[]; history: HistoryItem[] }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Section>('faq');
  const [open, setOpen] = useState(0);
  const base = useId();
  const tabs: [Section, string][] = [
    ['faq', t('billing.shop.details.faq')],
    ['history', t('billing.shop.details.history')],
  ];

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const next: Section = tab === 'faq' ? 'history' : 'faq';
    setTab(next);
    document.getElementById(`${base}-tab-${next}`)?.focus();
  };

  return (
    <section className="pl-details">
      <div className="pl-tabs" role="tablist">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            id={`${base}-tab-${id}`}
            type="button"
            role="tab"
            className="pl-tab"
            aria-selected={tab === id}
            aria-controls={`${base}-panel`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            onKeyDown={onKeyDown}
          >
            {label}
          </button>
        ))}
      </div>
      <div id={`${base}-panel`} role="tabpanel" aria-labelledby={`${base}-tab-${tab}`}>
        {tab === 'faq'
          ? <FaqList items={faq} open={open} onToggle={(index) => setOpen(open === index ? -1 : index)} />
          : <HistoryTable rows={history} />}
      </div>
    </section>
  );
}

/** Mobile: аккордеон из двух секций, всё свернуто. */
function DetailsAccordion({ faq, history }: { faq: FaqItem[]; history: HistoryItem[] }) {
  const { t } = useTranslation();
  const [section, setSection] = useState<Section | null>(null);
  const [open, setOpen] = useState(-1);
  const base = useId();
  const sections: [Section, string][] = [
    ['faq', t('billing.shop.details.faq')],
    ['history', t('billing.shop.details.history')],
  ];

  return (
    <section className="pl-sections">
      {sections.map(([id, label]) => {
        const expanded = section === id;
        return (
          <div key={id} className="pl-section">
            <h2 className="pl-section-h">
              <button
                type="button"
                className="pl-section-head"
                aria-expanded={expanded}
                aria-controls={`${base}-${id}`}
                onClick={() => setSection(expanded ? null : id)}
              >
                <span>{label}</span>
                <ChevronIcon />
              </button>
            </h2>
            <div id={`${base}-${id}`} className={`pl-section-body pl-section-body--${id}`} hidden={!expanded}>
              {id === 'faq'
                ? <FaqList items={faq} open={open} onToggle={(index) => setOpen(open === index ? -1 : index)} />
                : <HistoryList rows={history} />}
            </div>
          </div>
        );
      })}
    </section>
  );
}

export function BillingDetails({ overview, compact }: { overview: BillingOverview; compact: boolean }) {
  const faq = useFaq(overview);
  const history = useHistory(overview);
  return compact
    ? <DetailsAccordion faq={faq} history={history} />
    : <DetailsTabs faq={faq} history={history} />;
}

interface DonateCardProps {
  presets: number[];
  min: number;
  compact: boolean;
  /** Идёт выставление счёта за тариф — донат ждёт. */
  invoicePending: boolean;
  onPendingChange: (pending: boolean) => void;
}

export function DonateCard({ presets, min, compact, invoicePending, onPendingChange }: DonateCardProps) {
  const { t } = useTranslation();
  const f = useBillingFormat();
  const donate = useCreateDonationMutation();
  const submitting = useRef(false);
  const titleId = useId();
  // Три пресета + «своя», как в макете: остальные суммы вводятся вручную.
  const amounts = (presets.length ? presets : DONATE_PRESETS_FALLBACK).slice(0, 3);
  const [choice, setChoice] = useState<number | 'custom'>(amounts[1] ?? amounts[0]);
  const [custom, setCustom] = useState('');
  const amount = choice === 'custom' ? Number.parseInt(custom, 10) || 0 : choice;
  const minimum = Math.max(1, min || DONATE_MIN_FALLBACK);
  const valid = amount >= minimum;
  const busy = invoicePending || donate.isPending;

  const submit = () => {
    if (!valid || busy || submitting.current) return;
    submitting.current = true;
    onPendingChange(true);
    donate.mutate(amount, {
      onError: () => toast({ tone: 'error', title: t('plans.payErrorTitle'), message: t('plans.payErrorMessage') }),
      onSettled: () => {
        submitting.current = false;
        onPendingChange(false);
      },
    });
  };

  const label = donate.isPending ? t('plans.payPending')
    : amount === 0 ? t('billing.shop.donate.enter')
      : !valid ? t('billing.shop.donate.min', { amount: f.rub(minimum) })
        : t('billing.shop.donate.cta', { amount: f.rub(amount) });

  return (
    <section className="pl-card pl-donate" aria-labelledby={titleId}>
      <div className="pl-donate-head">
        <h2 id={titleId} className="pl-donate-title">{t('billing.shop.donate.title')} <em>Lomora</em></h2>
        <span className="pl-donate-note">{t(compact ? 'billing.shop.donate.noteShort' : 'billing.shop.donate.note')}</span>
      </div>
      <div className="pl-donate-amounts">
        {amounts.map((value) => (
          <button
            key={value}
            type="button"
            className="pl-amount"
            aria-pressed={choice === value}
            disabled={busy}
            onClick={() => setChoice(value)}
          >
            {f.rub(value)}
          </button>
        ))}
        <button
          type="button"
          className="pl-amount"
          aria-pressed={choice === 'custom'}
          disabled={busy}
          onClick={() => setChoice('custom')}
        >
          {t('billing.shop.donate.custom')}
        </button>
      </div>
      {choice === 'custom' && (
        <div className="pl-donate-field">
          <PlainField
            value={custom}
            onChange={(value) => setCustom(value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(event) => { if (event.key === 'Enter') submit(); }}
            inputMode="numeric"
            maxLength={6}
            placeholder={t('billing.shop.donate.placeholder')}
            disabled={busy}
            autoFocus
          />
          <span aria-hidden="true">₽</span>
        </div>
      )}
      <button
        type="button"
        className={cn('pl-donate-cta', valid && 'is-ready')}
        disabled={!valid || busy}
        aria-busy={donate.isPending}
        onClick={submit}
      >
        {label}
      </button>
    </section>
  );
}
